import { atom, useAtomValue, useSetAtom, useStore } from 'jotai';
import { useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import type { ChatItem, ToolUseItem } from '../../../contracts/harness.types';
import { artifactCanvasKey } from './artifactCanvasKey';
import {
  collectCreatedChatArtifacts,
  type CreatedChatArtifactPorts,
} from './collectCreatedChatArtifacts';
import { closeArtifactAtom, openArtifactAtom, type CreatedObjectRef } from './state';

const EMPTY: readonly CreatedObjectRef[] = [];
const createdChatArtifactsAtom = atom<
  ReadonlyMap<
    string,
    {
      owner: symbol;
      objects: readonly CreatedObjectRef[];
    }
  >
>(new Map());

export const useCreatedChatArtifacts = (
  projectId: string | undefined
): readonly CreatedObjectRef[] => {
  const projects = useAtomValue(createdChatArtifactsAtom);
  return projectId ? (projects.get(projectId)?.objects ?? EMPTY) : EMPTY;
};

const targetKey = (row: CreatedObjectRef): string =>
  row.target ? artifactCanvasKey(row.target) : '';

const sameRows = (
  a: readonly CreatedObjectRef[],
  b: readonly CreatedObjectRef[]
): boolean =>
  a.length === b.length &&
  a.every((row, index) => {
    const other = b[index]!;
    return (
      row.id === other.id &&
      row.title === other.title &&
      row.subtitle === other.subtitle &&
      row.createdAt === other.createdAt &&
      targetKey(row) === targetKey(other)
    );
  });

const isCompletedToolUse = (item: ChatItem): item is ToolUseItem =>
  item.kind === 'tool_use' && item.status === 'complete';

// `foldTranscript` allocates a fresh `ChatItem` for every row on every store
// change, so identity can never prove a completed call unchanged — its content
// has to.
const completedSignature = (items: readonly ToolUseItem[]): string =>
  items
    .map(
      (item) =>
        `${item.id}\u0000${item.status}\u0000${item.resultCreatedAt ?? ''}\u0000${
          typeof item.result?.content === 'string' ? item.result.content.length : 0
        }`
    )
    .join('\u0001');

/** The mounted chat owns these ephemeral rows; no display data enters storage. */
export const usePublishCreatedChatArtifacts = (
  projectId: string | undefined,
  items: readonly ChatItem[],
  ports: CreatedChatArtifactPorts
): void => {
  const intl = useIntl();
  const owner = useRef(Symbol('created-chat-artifacts')).current;
  const store = useStore();
  const setProjects = useSetAtom(createdChatArtifactsAtom);
  const completedRef = useRef<{ signature: string; items: ToolUseItem[] }>({
    signature: '',
    items: [],
  });
  const completed = useMemo(() => {
    const next = items.filter(isCompletedToolUse);
    const signature = completedSignature(next);
    const previous = completedRef.current;
    if (signature === previous.signature) return previous.items;
    completedRef.current = { signature, items: next };
    return next;
  }, [items]);
  const objects = useMemo(
    () => collectCreatedChatArtifacts(completed, { ...ports, intl }),
    [completed, intl, ports]
  );
  useEffect(() => {
    if (!projectId) return;
    setProjects((current) => {
      const published = current.get(projectId);
      if (published?.owner === owner && sameRows(published.objects, objects))
        return current;
      return new Map(current).set(projectId, { owner, objects });
    });
  }, [projectId, objects, owner, setProjects]);
  useEffect(
    () => () => {
      if (!projectId) return;
      setProjects((current) => {
        if (current.get(projectId)?.owner !== owner) return current;
        const next = new Map(current);
        next.delete(projectId);
        return next;
      });
      // The chat can swap without unmounting, so the departing chat's list
      // would otherwise stay open over rows that no longer exist.
      const open = store.get(openArtifactAtom);
      if (open?.type === 'created-objects' && open.projectId === projectId) {
        store.set(closeArtifactAtom);
      }
    },
    [projectId, owner, setProjects, store]
  );
};
